import { useState } from 'react';
import { Upload, FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const TwoMinuteReportGenerator = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearAll = () => {
    setFile(null);
    setReport(null);
    setLoading(false);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Security validations
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = ['text/csv', 'application/vnd.ms-excel'];
    const allowedExtensions = ['.csv'];
    
    if (uploadedFile.size > maxFileSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!allowedExtensions.includes(fileExtension) || !allowedTypes.includes(uploadedFile.type)) {
      if (!uploadedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Please upload a CSV file only');
        return;
      }
    }

    setFile(uploadedFile);
    setError(null);
  };

  const processData = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('csvFile', file);

      // Send file to server for processing using the new generic endpoint
      const response = await fetch(`${domainConfig.apiUrl}/api/reports/twominute`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      setReport(result.report);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTendencyFlag = (percentage) => {
    const pct = parseFloat(percentage);
    if (pct >= 80) return ' ❗';
    if (pct >= 70) return ' ⚠️';
    return '';
  };

  const downloadPDF = async () => {
    if (!report) return;

    try {
      await generateStandardizedPDF(
        REPORT_SECTIONS.TWO_MINUTE_REPORT,
        'two-minute-report.pdf',
        setError
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF: ' + error.message);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          component={Link}
          to="/reports"
          startIcon={<ArrowLeft size={20} />}
          sx={{ mb: 2 }}
        >
          Back to Reports
        </Button>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FileText size={32} />
          Two Minute Report Generator
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analyze offensive tendencies in critical 2-minute situations by quarter. Upload your CSV file containing play-by-play data.
        </Typography>
      </Box>

      {/* File Upload */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload CSV File
          </Typography>
          
          <Box sx={{ 
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            mb: 2,
            bgcolor: 'background.default'
          }}>
            <Upload size={48} style={{ color: '#666', marginBottom: '16px' }} />
            <Typography variant="h6" gutterBottom>
              Drop CSV file here or click to browse
            </Typography>
            <Button variant="contained" component="label" sx={{ mt: 2 }}>
              Select File
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>

          {file && (
            <Alert severity="info" sx={{ mb: 2 }}>
              File selected: {file.name}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              onClick={processData}
              disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <FileText size={20} />}
            >
              {loading ? 'Processing...' : 'Generate Report'}
            </Button>
            
            {(file || report) && (
              <Button variant="outlined" onClick={clearAll}>
                Clear All
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Report Display */}
      {report && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">
                Two Minute Report Results
              </Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<FileText size={20} />}
                onClick={downloadPDF}
              >
                Download PDF
              </Button>
            </Box>

            {/* Report Header */}
            <ReportHeader 
              title={`${report.teamName || 'TEAM'} 2-MINUTE REPORT`}
              fileName={file?.name}
            />
            
            <Grid container spacing={12}>
              {/* Foundation Data */}
              <Grid item xs={12}>
                <Card variant="outlined" data-section="foundation-section">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Foundation Data</Typography>
                    <Box component="ul" sx={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      '& li': {
                        '&:before': {
                          content: '"• "',
                          color: 'text.primary',
                          fontWeight: 'bold'
                        }
                      }
                    }}>
                      <li><Typography variant="h6" component="span">
                          <strong>Total Offensive Plays:</strong> {report.foundation?.totalPlays || 0}
                        </Typography>
                      </li>
                      <li><Typography variant="h6" component="span">
                          <strong>Run:</strong> {report.foundation?.runPlays || 0} plays ({report.foundation?.runPercent || 0}%)
                        </Typography>
                      </li>
                      <li><Typography variant="h6" component="span">
                          <strong>Pass:</strong> {report.foundation?.passPlays || 0} plays ({report.foundation?.passPercent || 0}%)
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="h6" component="span">
                          <strong>First Half 2-Minute (Q2):</strong> {report.foundation?.q2PlaysCount || 0} plays
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="h6" component="span">
                          <strong>Game 2-Minute (Q4):</strong> {report.foundation?.q4PlaysCount || 0} plays
                        </Typography>
                      </li>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Q2 Analysis */}
              <Grid item xs={12}>
                <Card variant="outlined" data-section="q2-analysis-section">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Final 2-Minutes of Half: Personnel & Play Type Breakdown</Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <div data-section="q2-personnel-section">
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Personnel Breakdown</Typography>
                          {report.q2Personnel?.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Personnel</TableCell>
                                    <TableCell align="right">Plays</TableCell>
                                    <TableCell align="right">Run %</TableCell>
                                    <TableCell align="right">Pass %</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {report.q2Personnel.map((group, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{group.personnel}</TableCell>
                                      <TableCell align="right">{group.total}</TableCell>
                                      <TableCell align="right">
                                        {group.runPercent}%{getTendencyFlag(group.runPercent)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {group.passPercent}%{getTendencyFlag(group.passPercent)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body1" color="text.secondary">No Q2 plays found.</Typography>
                          )}
                        </div>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <div data-section="q2-passing-section">
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Passing Targets</Typography>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Overall Passing:</strong> {report.q2Passing?.attempts || 0} Attempts | {report.q2Passing?.completions || 0} Completions | {report.q2Passing?.completionRate || '0.0'}% Completion Rate
                          </Typography>
                          
                          {report.q2Passing?.receivers?.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>Receiver Targets (Detailed Analysis)</Typography>
                              {report.q2Passing.receivers.map((receiver, index) => (
                                <Typography key={index} variant="body1" sx={{ ml: 2 }}>
                                  • {receiver.receiver}: {receiver.targets} targets ({receiver.deepBalls} deep balls)
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Q4 Analysis */}
              <Grid item xs={12}>
                <Card variant="outlined" data-section="q4-analysis-section">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Final 2-Minutes of Game: Personnel & Play Type Breakdown</Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <div data-section="q4-personnel-section">
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Personnel Breakdown</Typography>
                          {report.q4Personnel?.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Personnel</TableCell>
                                    <TableCell align="right">Plays</TableCell>
                                    <TableCell align="right">Run %</TableCell>
                                    <TableCell align="right">Pass %</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {report.q4Personnel.map((group, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{group.personnel}</TableCell>
                                      <TableCell align="right">{group.total}</TableCell>
                                      <TableCell align="right">
                                        {group.runPercent}%{getTendencyFlag(group.runPercent)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {group.passPercent}%{getTendencyFlag(group.passPercent)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body1" color="text.secondary">No Q4 plays found.</Typography>
                          )}
                        </div>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <div data-section="q4-passing-section">
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Passing Targets</Typography>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Overall Passing:</strong> {report.q4Passing?.attempts || 0} Attempts | {report.q4Passing?.completions || 0} Completions | {report.q4Passing?.completionRate || '0.0'}% Completion Rate
                          </Typography>
                          
                          {report.q4Passing?.receivers?.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>Receiver Targets (Detailed Analysis)</Typography>
                              {report.q4Passing.receivers.map((receiver, index) => (
                                <Typography key={index} variant="body1" sx={{ ml: 2 }}>
                                  • {receiver.receiver}: {receiver.targets} targets ({receiver.deepBalls} deep balls)
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Critical Tendencies */}
              <Grid item xs={12}>
                <Card variant="outlined" data-section="critical-tendencies-section">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>DEFENSIVE COORDINATOR NOTES - KEY TENDENCIES</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Critical Tendencies (≥70%)</Typography>
                    
                    {report.criticalTendencies?.length > 0 ? (
                      <Box>
                        {report.criticalTendencies.filter(t => t.quarter === 'Q2').length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                              First Half 2-Minute (Q2):
                            </Typography>
                            {report.criticalTendencies
                              .filter(t => t.quarter === 'Q2')
                              .map((tendency, index) => (
                                <Alert key={index} severity="warning" sx={{ mt: 1 }}>
                                  <strong>{tendency.personnel} Personnel = {tendency.tendency}</strong> 
                                  ({tendency.type} - {tendency.sample})
                                </Alert>
                              ))}
                          </Box>
                        )}
                        
                        {report.criticalTendencies.filter(t => t.quarter === 'Q4').length > 0 && (
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                              Game 2-Minute (Q4):
                            </Typography>
                            {report.criticalTendencies
                              .filter(t => t.quarter === 'Q4')
                              .map((tendency, index) => (
                                <Alert key={index} severity="warning" sx={{ mt: 1 }}>
                                  <strong>{tendency.personnel} Personnel = {tendency.tendency}</strong> 
                                  ({tendency.type} - {tendency.sample})
                                </Alert>
                              ))}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        No critical tendencies identified (≥70%).
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TwoMinuteReportGenerator;